import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Card } from "@/components/ui/card.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { cn } from "@/lib/utils.ts";
import { UploadCloud, FileDown, Eye, FileType } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FileUploadProps {
  onFileSelect: (file: File) => void;
}

export const FileUpload = ({ onFileSelect }: FileUploadProps) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (file.type === "text/csv" || file.name.endsWith(".csv")) {
          onFileSelect(file);
        }
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const handleDownloadExample = () => {
    const link = document.createElement('a');
    link.href = '/dyno-example.csv';
    link.download = 'dyno-example.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreviewExample = () => {
    fetch('/dyno-example.csv')
      .then(response => response.blob())
      .then(blob => {
        const file = new File([blob], 'dyno-example.csv', { type: 'text/csv' });
        onFileSelect(file);
      });
  };

  return (
    <Card className="bg-gradient-to-br from-background/50 to-muted/50 backdrop-blur-sm border border-muted-foreground/10 shadow-lg hover:shadow-xl transition-all">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground text-center">Upload Data Log</h3>
          <div
            {...getRootProps()}
            className={cn(
              "flex h-24 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all hover:border-primary/50 hover:bg-muted/30",
              isDragActive ? "border-primary bg-muted/30 scale-[0.99]" : "border-muted-foreground/20"
            )}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-2 px-4 text-center">
              <UploadCloud className={cn(
                "h-6 w-6 transition-all",
                isDragActive ? "text-primary scale-110" : "text-muted-foreground"
              )} />
              <p className="text-xs font-medium text-muted-foreground">
                {isDragActive ? "Drop your CSV file here" : "Drag & drop or click to upload your data log"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground">Example CSV</h3>
          <div className="flex flex-col gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-[150px] justify-start h-7 text-xs px-2"
              onClick={handleDownloadExample}
            >
              <FileDown className="mr-2 h-4 w-4" />
              Download Example
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-[150px] justify-start h-7 text-xs px-2 bg-[#31373d]/80 hover:bg-[#31373d]/60 text-white border-0"
              onClick={handlePreviewExample}
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview Example
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-muted-foreground text-center">Supported Formats</h3>
          <div className="flex justify-center items-center -mx-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a 
                    href="https://mhdtuning.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group relative w-full h-12 flex items-center justify-center px-1"
                  >
                    <img 
                      src="/images/mhd-logo.webp" 
                      alt="MHD Tuning" 
                      className="object-contain max-h-full max-w-full transition-all group-hover:scale-110 opacity-80 group-hover:opacity-100" 
                    />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">MHD Tuning software for BMW support F and G chassis</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <a 
                    href="https://www.bootmod3.net/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group relative w-full h-12 flex items-center justify-center px-1"
                  >
                    <img 
                      src="/images/bm3-logo.webp" 
                      alt="BootMod3" 
                      className="object-contain max-h-full max-w-full transition-all group-hover:scale-110 opacity-80 group-hover:opacity-100" 
                    />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">BootMod3 Tuning software for BMW support F and G chassis</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger>
                  <div className="group relative w-full h-12 flex items-center justify-center px-1">
                    <FileType 
                      className="w-10 h-10 text-muted-foreground transition-all group-hover:scale-110 opacity-80 group-hover:opacity-100" 
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Generic CSV files which support (Tq. / Nm. / RPM / Engine Speed / MAF g/s headers to render an dynosheet</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </Card>
  );
};
